import { CommonModule } from '@angular/common';
import { ENVIRONMENT_INITIALIZER, inject } from '@angular/core';
import { NgModule } from '@angular/core';
import { RouterModule } from '@angular/router';
import { EffectsModule } from '@ngrx/effects';
import { StoreModule } from '@ngrx/store';
import { AlertsComponent } from 'app/modules/darwin/components/alerts/alerts.component';
import { FooterComponent } from 'app/modules/darwin/components/footer/footer.component';
import { NavigationComponent } from 'app/modules/darwin/components/navigation/navigation.component';
import { DarwinActions } from 'app/modules/darwin/darwin.actions';
import { DarwinComponent } from 'app/modules/darwin/darwin.component';
import { AlertsConfig } from 'app/modules/darwin/models/alerts-config';
import { NavigationConfig } from 'app/modules/darwin/models/navigation-config';
import { SessionTokenEffects } from 'app/modules/darwin/store/session-token/session-token.effects';
import { SessionTokenReducer } from 'app/modules/darwin/store/session-token/session-token.reducer';
import { SessionTokenSelectors } from 'app/modules/darwin/store/session-token/session-token.selectors';
import { MainModule } from 'app/modules/main/main.module';
import { RegionCountryMappingService } from '@awin/navigation-renderer';
import { NavigationService } from '@awin/navigation-renderer';
import { NavigationConfigService } from '@awin/navigation-renderer';
import { LanguageService } from '@awin/navigation-renderer';
import { ActivityStreamService } from '@awin/navigation-renderer';
import { AccountSearchService } from '@awin/navigation-renderer';
import { NavigationRendererModule } from '@awin/navigation-renderer';
import { WindowWrapperService } from '@awin/shared';
import { PopupService } from '@awin/shared/dialogs';
import { SharedModule } from '@awin/shared';
import { environment } from 'environments/environment';
import { HttpClientInMemoryWebApiModule } from 'angular-in-memory-web-api';
import { InMemoryDataService } from 'app/fixtures/in-memory-data.service';
import { auth0authConfig } from 'app/oauth-config/auth0-oauth-config';

@NgModule({
  declarations: [
    AlertsComponent,
    DarwinComponent,
    FooterComponent,
    NavigationComponent,
  ],
  exports: [
    NavigationRendererModule,
    SharedModule,
  ],
  imports: [
    NavigationRendererModule.forRoot(
      {
        liveChatEnabled: environment.production,
        networkGroupSegment: 3,
        oidcClientId: auth0authConfig.clientId,
      },
      {
        name: environment.name,
        production: environment.production,
      }
    ),
    SharedModule.configure(
      {
        name: environment.name,
        production: environment.production,
      },
      {
        sessionTokenSelector: () => (SessionTokenSelectors as any).token,
        darwinSessionTokenInterceptUrls: [
          '/xhr-remind-me',
          '/xhr-update-locale',
        ],
      }
    ),
    environment.production
      ? []
      : HttpClientInMemoryWebApiModule.forRoot(InMemoryDataService, {
          delay: 100,
          passThruUnknownUrl: true,
        }),
    CommonModule,
    EffectsModule.forFeature([
      SessionTokenEffects,
    ]),
    MainModule,
    RouterModule,
    StoreModule.forFeature(
      SessionTokenSelectors.FEATURE,
      SessionTokenReducer.reducer()
    ),
  ],
  providers: [
    AccountSearchService,
    ActivityStreamService,
    LanguageService,
    NavigationConfigService,
    NavigationService,
    PopupService,
    RegionCountryMappingService,
    WindowWrapperService,
    {
      provide: AlertsComponent.DARWIN_ALERTS_CONFIG,
      useValue: {
        enabled: !!environment.loadDarwinAlerts,
      } as AlertsConfig
    },
    {
      provide: NavigationComponent.DARWIN_ADVERTISER_NAVIGATION_CONFIG,
      useValue: {
        enabled: !!environment.loadDarwinNavigation,
        activeMenuItem: 'linksAndTools',
        activeChildMenuItem: 'communication-centre',
      } as NavigationConfig
    },
    {
      provide: NavigationComponent.DARWIN_PUBLISHER_NAVIGATION_CONFIG,
      useValue: {
        enabled: !!environment.loadDarwinNavigation,
        activeMenuItem: 'linksAndTools',
        activeChildMenuItem: 'communication-centre',
      } as NavigationConfig
    },
    {
      multi: true,
      provide: SessionTokenEffects.LOAD,
      useValue: DarwinActions.moduleInitialized,
    },
    {
      // Needed only for the period on awin-auth->auth0 migration
      // once all the users are on auth0 we can hardcode clientId to index.html
      provide: ENVIRONMENT_INITIALIZER,
      multi: true,
      useFactory: () => {
        const window = inject(WindowWrapperService).nativeWindow;
        return () => {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (window as any).AWINChatbot?.init({
            getClientId: () => auth0authConfig.clientId,
          });
        }
      },
    },
  ],
})
export class DarwinModule {
}
